/**
 * @fileoverview Text-to-Speech UI Helper for blog content audio playback with visual controls.
 * 
 * This module provides the TextToSpeechHelper class which creates and manages the visual
 * audio player interface for blog text-to-speech functionality. It handles UI rendering,
 * user interactions, seekbar progress, and integrates with CommonFunctions for actual speech synthesis.
 * 
 * Key Features:
 * - **Singleton Pattern**: Single audio player instance across the application
 * - **Visual Audio Player**: Custom UI with play/pause, seekbar, and poster image
 * - **Sticky Positioning**: Player sticks to top on scroll with smart visibility
 * - **Seekbar Control**: Visual progress indicator with click-to-seek functionality
 * - **Brand Integration**: Dynamic styling based on brand configuration
 * - **Responsive Design**: Adapts to mobile and desktop viewports
 * - **Analytics Tracking**: Comprehensive event tracking via GTM dataLayer
 * - **Accessibility**: ARIA labels and keyboard navigation support
 * 
 * Architecture:
 * - Creates custom DOM elements for audio player interface
 * - Manages playback state and visual feedback
 * - Integrates with CommonFunctions for speech synthesis
 * - Handles scroll events for sticky positioning
 * - Injects brand-specific CSS dynamically
 * 
 * UI Components:
 * - Play/Pause button with SVG icons
 * - Seekbar with progress indicator
 * - Poster image from blog content
 * - Audio title and duration display
 * - Close button for dismissing player
 * 
 * @module text2speech/mainT2S
 * @requires text2speech/commonFunctions
 * @requires util/share
 * @requires util/device-detection
 * @author Roshan Jose
 * @version 2.0.0
 * @since 1.0.0
 * 
 * @example
 * ```typescript
 * // Initialize the text-to-speech UI
 * const ttsHelper = await TextToSpeechHelper.create();
 * 
 * // The player is automatically injected and ready to use
 * // User can click play button to start audio playback
 * 
 * // Programmatically control playback
 * ttsHelper.togglePlayButton();
 * ttsHelper.seekTo(30); // Seek to 30 seconds
 * ```
 */

import { CommonFunctions } from "./commonFunctions";
import {getBrandDetailsFromBrandJson } from "../util/share";
import { isTouchDevice } from "../util/device-detection";

/**
 * Text-to-Speech Helper class that provides audio playback functionality for blog content.
 * Implements singleton pattern to ensure only one instance exists throughout the application.
 * 
 * Features:
 * - Audio player with play/pause controls
 * - Visual seekbar with progress tracking
 * - Sticky positioning with scroll-based visibility
 * - Brand-specific styling and configuration
 * - Responsive design for mobile and desktop
 * - Integration with speech synthesis API
 * 
 * @class TextToSpeechHelper
 * @since 1.0.0
 * @example
 * ```typescript
 * const ttsHelper = await TextToSpeechHelper.create();
 * // The audio player is automatically injected into the DOM
 * ```
 */
export class TextToSpeechHelper {
    /** @private Static instance for singleton pattern */
    private static instance: TextToSpeechHelper;
    
    /** @private Reference to the play/pause button element */
    private playButton: HTMLButtonElement | null = null;
    
    /** @private Total audio duration in seconds (default: 10) */
    private audioDuration: number = 10; //in seconds
    
    /** @private Current playback position in seconds */
    private currentTime: number = 0;
    
    /** @private Interval ID for seekbar progress updates */
    private seekBarInterval: number | null = null;
    
    /** @private Current playing state of the audio */
    private isPlaying: boolean = false;
    
    /** @private Reference to the poster image element */
    private posterImage: HTMLImageElement | null = null;
    
    /** @private Reference to the duration text display element */
    private audioDurationText: HTMLSpanElement | null = null;
    
    /** @private Instance of CommonFunctions for speech synthesis */
    private commonFunctionInstance: CommonFunctions;
    
    /** @private Main container element for the audio player */
    private parentContainer: HTMLDivElement | null = null;
    
    /** @private Close button element for dismissing the player */
    private closeButton: HTMLDivElement | null = null;
    
    /** @private Reference to the page menu container for positioning */
    private menuContainer: HTMLDivElement | null = null;
    
    /** @private Calculated height of the menu for sticky positioning */
    private menuHeight: number = 0;

    /**
     * Gets the singleton instance of TextToSpeechHelper.
     * Returns existing instance if available.
     * 
     * @static
     * @returns {TextToSpeechHelper | null} The singleton instance or null if not initialized
     * @example
     * ```typescript
     * const helper = TextToSpeechHelper.getInstance();
     * ```
     */
    public static getInstance(): TextToSpeechHelper | null {
        return this.instance ?? null;
    }

    /**
     * Async factory method to create or get the singleton instance.
     * Handles async initialization of voices and DOM injection.
     * 
     * @static
     * @async
     * @returns {Promise<TextToSpeechHelper>} Promise resolving to the singleton instance
     * @example
     * ```typescript
     * const helper = await TextToSpeechHelper.create();
     * ```
     */
    public static async create(): Promise<TextToSpeechHelper> {
        if (!this.instance) {
            this.instance = new TextToSpeechHelper();
            await this.instance.initialize();
        }
        return this.instance;
    }

    /**
     * Private constructor for singleton pattern.
     * Only performs synchronous initialization.
     * 
     * @private
     * @constructor
     * @description
     * - Initializes CommonFunctions instance for speech synthesis
     * - Async operations are handled in initialize() method
     */
    private constructor() {
        this.commonFunctionInstance = CommonFunctions.getInstance();
    }

    /**
     * Async initialization method called by create().
     * Handles all asynchronous setup operations.
     * 
     * @private
     * @async
     * @returns {Promise<void>}
     * @description
     * - Loads available voices for text-to-speech
     * - Injects the audio player DOM elements
     * - Sets up event listeners for page unload and scroll
     */
    private async initialize(): Promise<void> {
        await this.commonFunctionInstance.getVoices();
        this.injectT2SDom();
        this.addBeforeUnloadEvent();
        this.addScrollListner();
    }

    /**
     * Creates an HTML image element with specified source and optional CSS class.
     * 
     * @private
     * @param {string} src - The image source URL
     * @param {string} [className] - Optional CSS class name to apply
     * @returns {HTMLImageElement} The created image element
     * @example
     * ```typescript
     * const img = this.createImg('/path/to/image.jpg', 'poster-image');
     * ```
     */
    private createImg(src: string, className?: string): HTMLImageElement {
        const img = document.createElement("img");
        img.src = src;
        if (className) img.className = className;
        return img;
    }

    /**
     * Creates a button element with an SVG icon.
     * Uses SVG sprite technique for efficient icon loading.
     * 
     * @private
     * @param {string} iconHref - The href reference to the SVG icon in the sprite
     * @returns {HTMLButtonElement} The created button element with SVG icon
     * @example
     * ```typescript
     * const playBtn = this.createButton('#play-icon');
     * ```
     */
    private createButton(iconHref: string): HTMLButtonElement {
        const btn = document.createElement("button");
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("aria-hidden", "true");
        const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
        use.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", iconHref);
        svg.appendChild(use);
        btn.appendChild(svg);
        return btn;
    }

    /**
     * Creates a seekbar element for audio progress visualization.
     * Contains a progress indicator that shows playback position.
     * 
     * @private
     * @returns {HTMLElement} The seekbar container with progress element
     * @description
     * Creates a two-part seekbar:
     * - Outer container (.seekbar) - full width background
     * - Inner progress (.seekbar-progress) - dynamic width indicator
     */
    private createSeekbar(): HTMLElement {
        const seekbar = document.createElement("div");
        seekbar.className = "seekbar";
        const progress = document.createElement("div");
        progress.className = "seekbar-progress";
        seekbar.appendChild(progress);
        return seekbar;
    }

    /**
     * Injects CSS styles for the text-to-speech player into the document head.
     * Prevents duplicate injection by checking for existing style element.
     * 
     * @private
     * @async
     * @returns {void}
     * @description
     * - Checks if styles already exist to prevent duplication
     * - Calculates menu height for proper sticky positioning
     * - Injects comprehensive CSS for player styling including:
     *   - Sticky positioning with dynamic top offset
     *   - Frosted glass effect with backdrop-filter
     *   - Responsive design for mobile and desktop
     *   - Hover effects and transitions
     *   - Seekbar styling and animations
     * @throws {Error} Logs warning if audio container position calculation fails
     */
    private injectT2SCss(): void {
        if (document.getElementById("t2s-style")) return; 
        this.getAudioContainerPosition().then(()=>{
            const style = document.createElement("style");
            style.id = "t2s-style";
            style.textContent = `
                .t2s-parent {
                    position: sticky;
                    top: ${this.menuHeight}px;
                    left: 0;
                    font-family: sans-serif;
                    margin: 1rem 0;
                    border-radius: 8px;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                    width: fit-content;
                }
                .t2s {
                    display: flex;
                    flex-direction: column;
                    background: #fff;
                    position: relative;

                    background: rgba(255, 255, 255, 0.4); /* Semi-transparent background */
                    border-radius: 15px; /* Rounded corners */
                    backdrop-filter: blur(6px); /* Frosted glass effect */
                    -webkit-backdrop-filter: blur(6px); /* For Safari support */
                    border: 1px solid rgba(255, 255, 255, 0.4); /* Subtle border */
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Soft shadow for depth */
                    padding: 1rem;
                    min-width: 300px;
                    width: fit-content;
                    text-align: right;
                }
                .t2s-close {
                    position: absolute;
                    top:-0.5rem;
                    right:-0.5rem;
                    cursor: pointer;
                }
                .t2s-close img {
                    width: 20px;
                    height: 20px;
                }
                .t2s-player {
                    display: flex;
                    gap: 1rem;
                }
                .t2s-player-poster{
                    width: 4rem;
                }
                .t2s-player-poster img {
                    max-width: 100%;
                    height: 80px;
                    object-fit: cover; /* zoom & crop edges */
                    object-position: center; /* keep centered */
                    border-radius: 0.25rem;
                    box-shadow: 4px 4px 14px -4px rgba(0,0,0,0.75);
                    -webkit-box-shadow: 4px 4px 14px -4px rgba(0,0,0,0.75);
                    -moz-box-shadow: 4px 4px 14px -4px rgba(0,0,0,0.75);
                }
                .t2s-player-container {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    text-align: left;
                }
                .t2s-player-container-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-top: 0.5rem;
                }
                .t2s-player-container-controls button {
                    background: none;
                    border: none;
                    cursor: pointer;
                    width: 20px;
                    height: 20px;
                    transition: 200ms ease-in-out;
                }
                .t2s-player-container-controls button:hover {
                    opacity: 0.8;
                }
                .t2s-player-container-controls button svg {
                    width: 20px;
                    height: 20px;
                }
                .t2s span{
                    font-size: 0.8em;
                    opacity: 0.8
                }
                .seekbar {
                    flex: 1;
                    height: 4px;
                    background: #eee;
                    border-radius: 2px;
                    position: relative;
                }
                .seekbar-progress {
                    height: 100%;
                    width: 0%;
                    background: var(--primary-500);
                    border-radius: 2px;
                }
                .t2s-footer {
                    position: absolute;
                    right: 1rem;
                    bottom: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }
                .t2s-footer span{
                    font-size: 0.6em;
                    font-style: italic;
                }
                .t2s-footer .t2s-brand {
                    width: 12px;
                    height: 12px;
                }
            `;
            document.head.appendChild(style);
        }).catch(err=>{
            console.warn(err);
        });
        

        
    }

    /**
     * Main method to inject the text-to-speech player DOM elements into the page.
     * Creates and assembles all UI components for the audio player.
     * 
     * @public
     * @returns {void}
     * @description
     * Creates a complete audio player interface including:
     * - Parent container with sticky positioning
     * - Close button (initially hidden)
     * - Poster image from blog or default favicon
     * - Play/pause button with SVG icons
     * - Seekbar for progress visualization
     * - Title display from blog content
     * - Footer with branding and estimated read time
     * 
     * @throws {Error} Logs warning if .entry-content container not found
     * @example
     * ```typescript
     * const helper = await TextToSpeechHelper.create();
     * // DOM is automatically injected during creation
     * ```
     */
    public injectT2SDom(): void {
        this.injectT2SCss(); // Ensure CSS is injected
        this.getAudioDuration();
        const container = document.querySelector(".entry-content");
        if (!container) {
            console.warn("No .entry-content found in DOM");
            return;
        }

        // Parent wrapper
        this.parentContainer = document.createElement("div");
        this.parentContainer.className = "t2s-parent";

        const t2s = document.createElement("div");
        t2s.className = "t2s";

        // Close
        this.closeButton = document.createElement("div");
        this.closeButton.className = "t2s-close";
        this.closeButton.style.display = "none";
        this.closeButton.addEventListener("click", () => this.clickedOnClose());
        this.closeButton.appendChild(
            this.createImg(
                "/brand/_assets/images/brand-img/close-circle.svg"
            )
        );

        // Poster
        const poster = document.createElement("div");
        poster.className = "t2s-player-poster";
        const imageSrc = this.commonFunctionInstance.getBlogImage();
        if(imageSrc){
            this.posterImage = this.createImg(imageSrc);
            poster.appendChild(this.posterImage);
        }
        else{
            this.posterImage = this.createImg('/brand/_assets/images/favicon/apple-touch-icon.png');
            this.posterImage.style.width = '100%';
            this.posterImage.style.height = 'auto';
            this.posterImage.style.boxShadow = 'none';
            poster.appendChild(this.posterImage);
        }

        // Controls
        const controls = document.createElement("div");
        controls.className = "t2s-player-container-controls";

        this.playButton = this.createButton(
            "/brand/_assets/images/icons/hero-icon-sprite.svg#play-icon"
        );
        this.playButton.id = "t2s-playing";
        this.playButton.addEventListener("click", () => this.togglePlayButton());
        controls.appendChild(this.playButton);
        controls.appendChild(this.createSeekbar());

        const playerContainer = document.createElement("div");
        playerContainer.className = "t2s-player-container";
        const title = document.createElement("span");
        title.textContent = this.commonFunctionInstance.getBlogTitle();
        playerContainer.appendChild(title);
        playerContainer.appendChild(controls);

        const player = document.createElement("div");
        player.className = "t2s-player";
        player.appendChild(poster);
        player.appendChild(playerContainer);

        // Footer
        const footer = document.createElement("div");
        footer.className = "t2s-footer";
        footer.appendChild(
            this.createImg(
                "/brand/_assets/images/favicon/apple-touch-icon.png",
                "t2s-brand"
            )
        );
        this.audioDurationText = document.createElement("span");
        this.audioDurationText.textContent = `Estimated Read Time : ${this.commonFunctionInstance.getEstimatedPlaybackTime()}`;
        this.audioDurationText.className = "t2s-summary";
        footer.appendChild(this.audioDurationText);

        // Assemble
        t2s.appendChild(this.closeButton);
        t2s.appendChild(player);
        t2s.appendChild(footer);
        this.parentContainer.appendChild(t2s);
        //add this to the srating of the div
        container.prepend(this.parentContainer);
    }

    /**
     * Toggles the play/pause state of the audio player.
     * Handles three states: playing, paused, and stopped.
     * 
     * @public
     * @returns {void}
     * @description
     * State transitions:
     * - If playing: Pauses speech, shows play icon, stops seekbar progress
     * - If paused: Resumes speech, shows pause icon, starts seekbar progress
     * - If stopped: Starts new speech, shows pause icon, starts seekbar progress
     * 
     * Updates button appearance and manages seekbar animation accordingly.
     * Integrates with CommonFunctions for actual speech synthesis control.
     * 
     * @example
     * ```typescript
     * // Called when user clicks the play/pause button
     * helper.togglePlayButton();
     * ```
     */
    togglePlayButton(){
        if (!this.playButton) return;

        if (this.commonFunctionInstance.isPlaying) {
            // Pause the speech
            this.commonFunctionInstance.pauseSpeech(this.playButton);
            this.playButton.innerHTML = '<svg aria-hidden="true"><use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#play-icon"></use></svg>';
            this.playButton.id = "t2s-playing";
            this.isPlaying = false;
            this.commonFunctionInstance.analyticsEvent('pause', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.currentTime);
            this.stopSeekBarProgress();
        } else if (this.commonFunctionInstance.isPaused) {
            // Resume the speech
            this.commonFunctionInstance.resumeSpeech(this.playButton);
            this.playButton.innerHTML = '<svg aria-hidden="true"><use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#pause-icon"></use></svg>';
            this.playButton.id = "t2s-paused";
            this.isPlaying = true;
            this.commonFunctionInstance.analyticsEvent('play', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration());
            this.startSeekBarProgress();
        } else {
            // Start new speech
            this.commonFunctionInstance.speakText(this.playButton);
            this.playButton.innerHTML = '<svg aria-hidden="true"><use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#pause-icon"></use></svg>';
            this.playButton.id = "t2s-paused";
            this.isPlaying = true;
            this.commonFunctionInstance.analyticsEvent('play', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration());
            this.startSeekBarProgress();
        }
    }

    /**
     * Starts the seekbar progress animation with interval-based updates.
     * Creates a recurring interval that updates the visual progress every 100ms.
     * 
     * @public
     * @returns {void}
     * @description
     * Progress Update Mechanism:
     * - Sets up 100ms interval for smooth visual updates
     * - Increments currentTime by 0.1 seconds per tick
     * - Updates seekbar visual representation
     * - Automatically stops when audio completes
     * 
     * Performance Considerations:
     * - Uses requestAnimationFrame-like interval for smooth updates
     * - Prevents multiple intervals by checking existing interval
     * - Cleans up interval on audio completion
     * 
     * @example
     * ```typescript
     * // Start seekbar animation when audio plays
     * helper.startSeekBarProgress();
     * ```
     * 
     * @see {@link stopSeekBarProgress} For stopping the animation
     * @see {@link updateSeekBarVisual} For visual update logic
     */
    startSeekBarProgress(): void {
        if (this.seekBarInterval) {
            this.stopSeekBarProgress(); // Clear any existing interval
        }

        this.seekBarInterval = window.setInterval(() => {
            if (this.isPlaying && this.currentTime < this.audioDuration) {
                this.currentTime += 1; // Increment by 1 second
                this.updateSeekBarVisual();
                
                // Auto-stop when audio completes
                if (this.currentTime >= this.audioDuration) {
                    this.onAudioComplete();
                }
            }
        }, 1000);
    }

    /**
     * Stops the seekbar progress animation and clears the interval.
     * Prevents memory leaks by properly cleaning up the interval timer.
     * 
     * @public
     * @returns {void}
     * @description
     * Cleanup Operations:
     * - Clears the active interval using clearInterval
     * - Resets seekBarInterval to null for garbage collection
     * - Prevents multiple intervals from running simultaneously
     * 
     * Called When:
     * - User pauses audio playback
     * - Audio playback completes
     * - User closes the audio player
     * - Before starting a new interval
     * 
     * @example
     * ```typescript
     * // Stop seekbar when pausing audio
     * helper.stopSeekBarProgress();
     * ```
     * 
     * @see {@link startSeekBarProgress} For starting the animation
     */
    stopSeekBarProgress(): void {
        if (this.seekBarInterval) {
            clearInterval(this.seekBarInterval);
            this.seekBarInterval = null;
        }
    }

    /**
     * Updates the visual seekbar progress based on current playback time.
     * Calculates and applies the progress percentage to the seekbar width.
     * 
     * @public
     * @returns {void}
     * @description
     * Visual Update Process:
     * - Queries the seekbar progress element from DOM
     * - Calculates percentage using current time vs total duration
     * - Updates the width CSS property to reflect progress
     * - Handles edge cases (missing element, invalid values)
     * 
     * Progress Calculation:
     * - Uses calculateSeekbarPercent() for accurate percentage
     * - Clamps value between 0-100%
     * - Applies smooth visual transition via CSS
     * 
     * @example
     * ```typescript
     * // Update seekbar to show current progress
     * helper.updateSeekBarVisual();
     * ```
     * 
     * @see {@link calculateSeekbarPercent} For percentage calculation logic
     */
    updateSeekBarVisual(): void {
        const seekBar = document.querySelector(".seekbar-progress") as HTMLElement;
        if (!seekBar) return;

        const percentage = this.calculateSeekbarPercent();
        seekBar.style.width = `${percentage}%`;
    }

    /**
     * Calculates the current seekbar percentage based on playback progress.
     * Provides accurate progress calculation with boundary protection.
     * 
     * @public
     * @returns {number} Percentage (0-100) of audio progress
     * @description
     * Calculation Logic:
     * - Divides current time by total duration
     * - Multiplies by 100 to get percentage
     * - Clamps result between 0 and 100
     * - Handles edge cases (zero duration, overflow)
     * 
     * Edge Case Handling:
     * - Returns 0 if duration is 0 (prevents division by zero)
     * - Returns 100 if current time exceeds duration
     * - Returns 0 if current time is negative
     * 
     * @example
     * ```typescript
     * // Get current progress percentage
     * const progress = helper.calculateSeekbarPercent();
     * console.log(`Audio is ${progress}% complete`);
     * ```
     * 
     * @see {@link updateSeekBarVisual} For visual application of percentage
     */
    calculateSeekbarPercent(): number {
        if (this.audioDuration <= 0) return 0;
        const percentage = (this.currentTime / this.audioDuration) * 100;
        return Math.min(percentage, 100); // Cap at 100%
    }

    /**
     * Handles audio completion by resetting player state and UI.
     * Called automatically when audio playback reaches the end.
     * 
     * @public
     * @returns {void}
     * @description
     * Completion Actions:
     * - Resets current time to 0
     * - Sets isPlaying to false
     * - Stops seekbar progress animation
     * - Sends 'audio_ended' analytics event
     * - Resets play button to initial state
     * - Resets seekbar visual to beginning
     * 
     * Analytics Tracking:
     * - Tracks completion with full duration as elapsed time
     * - Captures audio title and total duration
     * - Sends to GTM dataLayer for engagement metrics
     * 
     * @example
     * ```typescript
     * // Automatically called when audio ends
     * // Can also be called manually to reset player
     * helper.onAudioComplete();
     * ```
     * 
     * @see {@link stopSeekBarProgress} For stopping animation
     * @see {@link displayPlaybutton} For UI reset
     */
    onAudioComplete(): void {
        this.currentTime = 0;
        this.isPlaying = false;
        this.stopSeekBarProgress();
        this.commonFunctionInstance.analyticsEvent('end', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.commonFunctionInstance.calculatePlaybackDuration());
        // Reset button to play state
        this.displayPlaybutton();
        
        // Reset seekbar to beginning
        this.updateSeekBarVisual();
    }

    /**
     * Sets the audio duration and resets playback state.
     * Ensures minimum duration of 1 second to prevent division errors.
     * 
     * @public
     * @param {number} duration - Duration in seconds (minimum 1)
     * @returns {void}
     * @description
     * Duration Setting Process:
     * - Applies minimum constraint of 1 second
     * - Resets current time to 0
     * - Updates seekbar visual to reflect reset
     * 
     * Safety Features:
     * - Prevents zero or negative duration (uses Math.max)
     * - Automatically resets playback position
     * - Updates UI immediately
     * 
     * @example
     * ```typescript
     * // Set audio duration to 3 minutes
     * helper.setAudioDuration(180);
     * ```
     */
    setAudioDuration(duration: number): void {
        this.audioDuration = Math.max(duration, 1); // Minimum 1 second
        this.currentTime = 0;
        this.updateSeekBarVisual();
    }

    /**
     * Gets the current playback time position.
     * 
     * @public
     * @returns {number} Current time in seconds
     * @description
     * Returns the current playback position which is:
     * - Updated every second during playback
     * - Used for seekbar progress calculation
     * - Reset to 0 on audio completion
     * - Clamped between 0 and audioDuration
     * 
     * @example
     * ```typescript
     * const currentPos = helper.getCurrentTime();
     * console.log(`Currently at ${currentPos} seconds`);
     * ```
     */
    getCurrentTime(): number {
        return this.currentTime;
    }

    /**
     * Gets the total audio duration from speech synthesis calculation.
     * Calculates duration based on word count and speech rate.
     * 
     * @public
     * @returns {number} Duration in seconds
     * @description
     * Duration Calculation:
     * - Delegates to CommonFunctions.calculatePlaybackDuration()
     * - Based on word count and speech rate
     * - Cached in audioDuration property
     * - Used for seekbar percentage calculations
     * 
     * @example
     * ```typescript
     * const duration = helper.getAudioDuration();
     * console.log(`Audio will play for ${duration} seconds`);
     * ```
     * 
     * @see {@link CommonFunctions.calculatePlaybackDuration} For calculation logic
     */
    getAudioDuration(): number {
        this.audioDuration = this.commonFunctionInstance.calculatePlaybackDuration();

        return this.audioDuration;
    }

    /**
     * Seeks to a specific time position in the audio.
     * Clamps the time value within valid bounds and updates UI.
     * 
     * @public
     * @param {number} time - Time in seconds to seek to
     * @returns {void}
     * @description
     * Seek Behavior:
     * - Clamps time between 0 and audioDuration
     * - Updates currentTime property
     * - Immediately updates seekbar visual
     * - Does not affect playback state (play/pause)
     * 
     * Boundary Protection:
     * - Minimum: 0 seconds (start)
     * - Maximum: audioDuration (end)
     * - Uses Math.max and Math.min for clamping
     * 
     * @example
     * ```typescript
     * // Seek to 30 seconds
     * helper.seekTo(30);
     * 
     * // Seek to middle of audio
     * helper.seekTo(helper.getAudioDuration() / 2);
     * ```
     */
    seekTo(time: number): void {
        this.currentTime = Math.max(0, Math.min(time, this.audioDuration));
        this.updateSeekBarVisual();
    }

    /**
     * Handles the close button click event.
     * Stops audio playback and resets the player to static positioning.
     * 
     * @public
     * @returns {void}
     * @description
     * Cleanup operations when closing the player:
     * - Resets play button to initial state
     * - Cancels any ongoing speech synthesis
     * - Stops seekbar progress animation
     * - Changes container from sticky to static positioning
     * - Removes close button from DOM
     * 
     * @example
     * ```typescript
     * // Called when user clicks the close button
     * helper.clickedOnClose();
     * ```
     */
    clickedOnClose(){
        if(this.parentContainer){
            this.displayPlaybutton();
            globalThis.speechSynthesis.cancel();
            this.stopSeekBarProgress();
            this.parentContainer.style.position = 'static';
        }
        const closeButton = document.querySelector(".t2s-close");
        if(closeButton){
            closeButton.remove();
        }
        if(this.currentTime === 0){
            this.commonFunctionInstance.analyticsEvent('ignored', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.commonFunctionInstance.calculatePlaybackDuration());
        }
        else{
            this.commonFunctionInstance.analyticsEvent('pause', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.currentTime);
        }
        
        globalThis.speechSynthesis.cancel();
    }

    /**
     * Adds event listener for page unload to cleanup speech synthesis.
     * Prevents audio from continuing when user navigates away.
     * 
     * @private
     * @returns {void}
     * @description
     * Registers a beforeunload event listener that cancels any ongoing
     * speech synthesis to prevent audio from playing after page navigation.
     */
    addBeforeUnloadEvent(){
        globalThis.addEventListener("beforeunload", () => this.beforeunload());
    }
    
    /**
     * Cleanup method called before page unload.
     * Cancels any ongoing speech synthesis.
     * 
     * @private
     * @returns {void}
     * @description
     * Ensures speech synthesis is properly stopped when user:
     * - Navigates to another page
     * - Refreshes the page
     * - Closes the browser tab/window
     */
    beforeunload(){
        if(this.currentTime === 0){
            this.commonFunctionInstance.analyticsEvent('ignored', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.commonFunctionInstance.calculatePlaybackDuration());
        }
        else{
            this.commonFunctionInstance.analyticsEvent('pause', this.commonFunctionInstance.getBlogTitle(), this.commonFunctionInstance.calculatePlaybackDuration(), this.currentTime);
        }
        globalThis.speechSynthesis.cancel();
    }

    /**
     * Resets the play button to its initial play state.
     * Updates both the visual icon and the button ID.
     * 
     * @private
     * @returns {void}
     * @description
     * Resets button appearance to show:
     * - Play icon (triangle)
     * - ID set to "t2s-playing"
     * 
     * Used when audio completes or is manually stopped.
     */
    displayPlaybutton(){
        if (this.playButton) {
            this.playButton.id = "t2s-playing";
            this.playButton.innerHTML = '<svg aria-hidden="true"><use xlink:href="/brand/_assets/images/icons/hero-icon-sprite.svg#play-icon"></use></svg>';
        }
    }

    /**
     * Adds scroll event listener to manage sticky player visibility.
     * Monitors scroll position to show/hide close button appropriately.
     * 
     * @private
     * @returns {void}
     * @description
     * Registers a scroll event listener that calls the scroll() method
     * to handle dynamic UI changes based on scroll position.
     */
    addScrollListner(){
        globalThis.addEventListener("scroll", () => this.scroll());
    }

    /**
     * Calculates the optimal positioning for the audio container based on page layout.
     * Determines menu height for proper sticky positioning of the player.
     * 
     * @private
     * @async
     * @returns {Promise<void>}
     * @description
     * Positioning logic:
     * 1. Gets concept code from page to determine brand configuration
     * 2. Checks brand settings for sticky header preferences:
     *    - If sticky header disabled on desktop: sets minimal height (8px)
     *    - If sticky header disabled on mobile: sets minimal height (8px)
     * 3. Otherwise, measures actual menu container height
     * 4. Adds 8px padding to final height calculation
     * 
     * This ensures the audio player positions correctly relative to:
     * - Fixed navigation headers
     * - Brand-specific layout configurations
     * - Device-specific display preferences
     * 
     * @throws {Error} Silently handles errors in brand data fetching
     */
    async getAudioContainerPosition(){
        const conceptCode = (document.getElementById('conceptCode') as HTMLInputElement)?.value;
        const brandData = await getBrandDetailsFromBrandJson(conceptCode);
        if(brandData?.disable_sticky_header && !isTouchDevice()){
            this.menuHeight = 8;
            return;
        }
        if(brandData?.disable_sticky_header_mobile && isTouchDevice()){
            this.menuHeight = 8;
            return;
        }

        if(!this.menuContainer){
            this.menuContainer = document.querySelector(".second-head");
        }
        if(this.menuContainer){
            this.menuHeight = this.menuContainer.getBoundingClientRect().height;
        }
        this.menuHeight = Math.ceil(this.menuHeight) + 8;
    }

    /**
     * Handles scroll events to manage close button visibility.
     * Shows/hides close button based on sticky positioning state.
     * 
     * @private
     * @returns {void}
     * @description
     * Scroll behavior logic:
     * 1. Checks if audio container has reached the sticky position threshold
     * 2. Calculates threshold as: menuHeight + 16px buffer
     * 3. Shows close button when:
     *    - Container has reached the top threshold
     *    - Container is in sticky positioning mode (not static)
     * 4. Hides close button when:
     *    - Container is below the threshold
     *    - Container is in static positioning mode
     * 
     * This provides intuitive UX where the close button only appears
     * when the player is "stuck" to the top of the viewport.
     * 
     * @example
     * ```typescript
     * // Called automatically on scroll events
     * // User scrolls down -> close button appears when player sticks
     * // User scrolls up -> close button disappears when player unsticks
     * ```
     */
    scroll(){
        // Check if the parent container has reached the top of the page
        
        if (this.parentContainer) {
            const containerRect = this.parentContainer.getBoundingClientRect();
            const hasReachedTop = containerRect.top <= this.menuHeight + 16; 
            
            // Only show close button if container has reached the top and is sticky
            if (hasReachedTop && this.parentContainer.style.position !== 'static') {
                if (this.closeButton) {
                    this.closeButton.style.display = 'block';
                }
            } else {
                // Hide close button when not at top
                if (this.closeButton) {
                    this.closeButton.style.display = 'none';
                }
            }
        }
    }
}
